// @flow
import * as React from 'react';
import styled, { withTheme } from 'styled-components';
import { translate } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import CodeSnippet from 'common/components/CodeSnippet';
import TitledCard from 'common/components/cards/TitledCard';
import RaisedButton from 'material-ui/RaisedButton';
import {
  radioAnalyticsInstallationSnippet,
  createCompleteRadioAnalyticsInstallationSnippetFor,
  SNIPPET_PLAYERS,
  type SnippetPlayer,
} from '../../content/radioAnalyticsSnippet';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
`;

const SectionWrapper = styled(Wrapper)`
  padding: ${({ theme }) => theme.metrics.margin}px;
`;

const CodeSnippetWrapper = styled.div`
  margin: ${({ theme }) => theme.metrics.smallMargin}px 0;
`;

const Text = styled.p`
  font-size: ${({ theme }) => theme.fonts.style.midSmall.fontSize}px;
  color: ${({ theme }) => theme.colors.primaryText};
`;

const List = styled.ol`
  font-size: ${({ theme }) => theme.fonts.style.midSmall.fontSize}px;
  color: ${({ theme }) => theme.colors.primaryText};
`;

const Bold = styled.strong`
  font-weight: ${({ theme }) => theme.fonts.weights.bolder};
`;

const Emphasis = styled.em`
  color: ${({ theme }) => theme.colors.primary};
  font-style: normal;
`;

const ImageWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border: solid 2px ${({ theme }) => theme.colors.accent};
  margin: 0 auto;
`;

const CodeSnippetButtonsWrapper = styled.div`
  width: 100%;  
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

type Props = {
  t: any;
  theme: Object;
  callSign: string;

  onConfigurationCompleted: () => any;
}

type State = {
  snippetPlayer: SnippetPlayer;
  snippet: string;
}

class RadioAnalyticsConfiguration extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const defaultSnippetPlayer = 'audio/video';

    this.state = {
      snippetPlayer: defaultSnippetPlayer,
      snippet: createCompleteRadioAnalyticsInstallationSnippetFor(props.callSign, defaultSnippetPlayer),
    };
  }

  onChangePlayerSnippet = (player: SnippetPlayer) => {
    this.setState({
      snippetPlayer: player,
      snippet: createCompleteRadioAnalyticsInstallationSnippetFor(this.props.callSign, player),
    });
  }

  renderMarkdown(markdown: string) {
    const markdownRenderers = { strong: Bold, emphasis: Emphasis, paragraph: Text, list: List };

    return <ReactMarkdown source={markdown} renderers={markdownRenderers} />;
  }

  renderPlayerButtonSnippet = (snippetPlayer: SnippetPlayer) => {
    const { theme, t } = this.props;
    const playerButtonStyle = { fontSize: theme.fonts.style.xsmall.fontSize, fontWeight: theme.fonts.weights.bolder };

    return (
      <RaisedButton
        key={snippetPlayer}
        primary={snippetPlayer === this.state.snippetPlayer}
        labelStyle={playerButtonStyle}
        label={t(snippetPlayer)}
        onClick={() => this.onChangePlayerSnippet(snippetPlayer)}
      />
    );
  }

  render() {
    const { t, theme, callSign } = this.props;

    return (
      <Wrapper>
        <SectionWrapper>
          <Text>{t('installingRadioAnalytics')}</Text>
          <CodeSnippetWrapper>
            <CodeSnippet language="javascript">
              {radioAnalyticsInstallationSnippet}
            </CodeSnippet>
          </CodeSnippetWrapper>
        </SectionWrapper>
        <TitledCard title={t('configuration')}>
          <CodeSnippetWrapper>
            {this.renderMarkdown(t('configurationMarkdown', { callSign: callSign }))}
            <CodeSnippetButtonsWrapper>
              {SNIPPET_PLAYERS.map(this.renderPlayerButtonSnippet)}
            </CodeSnippetButtonsWrapper>
            <CodeSnippet language="javascript">
              {this.state.snippet}
            </CodeSnippet>
          </CodeSnippetWrapper>
        </TitledCard>
        <TitledCard title={t('verification')}>
          {this.renderMarkdown(t('verificationMarkdown'))}
          <ImageWrapper>
            <Image alt="Network usage" src={theme.images.verifyNetworkUsage} />
          </ImageWrapper>
        </TitledCard>
        <TitledCard title={t('notes')}>
          {this.renderMarkdown(t('notesMarkdown'))}
        </TitledCard>
      </Wrapper>
    );
  }
}

export default withTheme(translate('radioAnalyticsConfiguration')(RadioAnalyticsConfiguration));



// WEBPACK FOOTER //
// ./src/statsradio/modules/radioConfiguration/components/playerConfigurationForm/RadioAnalyticsConfiguration.js